import { useMutation, useQuery } from '@tanstack/react-query'

import { API_RESOURCE } from '@/constants/api.constant'
import { useCrud } from '@/hooks/useCrud'
import DocumentTemplateService from './documentTemplate.service'

// Hoisted so react-query can memoise the selected value. An inline selector is
// a new function every render, which makes the hook hand back a new array each
// time — and the editor rebuilds its extensions when the catalogue changes
// identity.
const EMPTY_VARIABLES = []
const selectVariables = (response) => response?.data?.variables ?? EMPTY_VARIABLES

const useDocumentTemplate = () => {
  const documentTemplateService = new DocumentTemplateService()
  const baseCrud = useCrud(API_RESOURCE.DOCUMENT_TEMPLATE, documentTemplateService)

  /**
   * The variable catalogue for a template type. Cached indefinitely: it is a
   * server constant, not data, and the editor needs it before it can render a
   * single chip.
   */
  const useFetchVariables = (type) =>
    useQuery({
      queryKey: [...baseCrud.queryKey.all, 'variables', type],
      queryFn: () => documentTemplateService.fetchVariables(type),
      enabled: !!type,
      staleTime: Infinity,
      select: selectVariables,
    })

  const usePreviewDocumentTemplate = () =>
    useMutation({
      mutationFn: documentTemplateService.previewWithSampleData,
    })

  return {
    useFetchDocumentTemplates: baseCrud.useFetchAll,
    useFetchDocumentTemplate: baseCrud.useFetchOne,
    useCreateDocumentTemplate: baseCrud.useCreate,
    useUpdateDocumentTemplate: baseCrud.useUpdate,
    useFetchVariables,
    usePreviewDocumentTemplate,
  }
}

export default useDocumentTemplate
